import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "components";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  API_URL_agent,
  API_URL_department,
  API_URL_group,
} from "constants/api";
import { agentReducers } from "redux/agentSlice";
import { getData, postData, putData } from "actions";
import { departmentReducers } from "redux/departmentSlice";
import { groupReducers } from "redux/groupSlice";
import { AuthContext } from "context/AuthContext";

const FormAgents = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const { getGroupResult, getDepartmentResult } = useSelector((state) => ({
    getGroupResult: state.group.getGroupResult,
    getDepartmentResult: state.department.getDepartmentResult,
  }));

  const getGroup = useCallback(
    async (params) => {
      getData(
        API_URL_group,
        params,
        { dispatch, redux: groupReducers },
        "GET_GROUP"
      );
    },
    [dispatch]
  );

  const getDepartment = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      username: location.state ? location.state.item.username : "",
      first_name: location.state ? location.state.item.first_name : "",
      last_name: location.state ? location.state.item.last_name : "",
      email: location.state ? location.state.item.email : "",
      ext: location.state ? location.state.item.ext : "",
      country_code: location.state ? location.state.item.country_code : "",
      phone_number: location.state ? location.state.item.phone_number : "",
      mobile: location.state ? location.state.item.mobile : "",
      groups: location.state ? location.state.item.groups : [],
      primary_dpt: location.state ? location.state.item.primary_dpt : "",
      agent_tzone: location.state ? location.state.item.agent_tzone : "",
      is_active: location.state ? location.state.item.is_active : "",
      agent_sign: location.state ? location.state.item.agent_sign : "",
      send_password_via_email: true,
      type: "agent",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Username is required"),
      first_name: Yup.string().required("Firstname is required"),
      email: Yup.string().required("Email is required"),
      primary_dpt: Yup.string().required("Primary Department is required"),
      is_active: Yup.string().required("Status is required"),
      groups: Yup.array().min(1, "Groups is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_agent,
          formData,
          { dispatch, redux: agentReducers },
          "ADD_AGENT",
          () => {
            navigate("/administrator/staff/agents");
          }
        );
      } else {
        putData(
          `${API_URL_agent}${location.state.item.id}/`,
          formData,
          { dispatch, redux: agentReducers },
          "ADD_AGENT",
          () => {
            navigate("/administrator/staff/agents");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    const params = `?limit=${9999999}&all=1`;
    getGroup(params);
    getDepartment(params);
  }, [getGroup]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Agents"}
        excludedPaths={["staff", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          {location.state ? "Edit an Agent" : "Create an agent"}
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  User Name <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.username && formik.errors.username && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.username}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  First name <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    id="first_name"
                    type="text"
                    name="first_name"
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.first_name && formik.errors.first_name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.first_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Last name
                </label>
                <div>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.last_name && formik.errors.last_name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.last_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Email address <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="text-xs md:-text-sm lg:text-sm">
                  Phone Number
                </label>
                <div>
                  <input
                    id="phone_number"
                    name="phone_number"
                    type="number"
                    ref={(ref) => (inputRefs.current[4] = ref)}
                    value={formik.values.phone_number}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.phone_number &&
                    formik.errors.phone_number && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.phone_number}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Primary department <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="primary_dpt"
                    name="primary_dpt"
                    ref={(ref) => (inputRefs.current[5] = ref)}
                    value={formik.values.primary_dpt}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a department</option>
                    {getDepartmentResult.results &&
                      getDepartmentResult.results.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.primary_dpt && formik.errors.primary_dpt && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.primary_dpt}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col flex-wrap">
                  <div>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="is_active"
                        type="radio"
                        name="is_active"
                        checked={formik.values.is_active === true}
                        onChange={() => formik.setFieldValue("is_active", true)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2 mr-5">Active</span>
                    </label>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="is_active"
                        type="radio"
                        name="is_active"
                        checked={formik.values.is_active === false}
                        onChange={() =>
                          formik.setFieldValue("is_active", false)
                        }
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2">Inactive</span>
                    </label>
                  </div>
                  {formik.touched.is_active && formik.errors.is_active && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.is_active}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Group Permission <span className="text-red-500">*</span>
                </label>
                <div className="max-h-40 overflow-y-auto">
                  <div className="flex flex-col flex-wrap">
                    <div>
                      {getGroupResult.results &&
                        getGroupResult.results.map((item, index) => (
                          <div key={index}>
                            <label className="text-xs md:-text-sm lg:text-sm">
                              <input
                                id={item.name}
                                type="radio" // Menggunakan radio button
                                name="selectedGroup" // Memberikan nama yang sama pada semua radio button
                                value={item.id} // Nilai yang akan disetel jika dipilih
                                checked={formik.values.groups.includes(item.id)} // Memeriksa apakah item ini dipilih
                                onChange={(e) => {
                                  formik.setFieldValue("groups", [item.id]); // Menetapkan item terpilih sebagai satu-satunya item dalam array
                                }}
                                className="form-radio mr-2 mt-2"
                              />
                              {item.name}
                            </label>
                            <br />
                          </div>
                        ))}

                      {formik.touched.groups && formik.errors.groups && (
                        <div className="text-red-500 dark:text-red-300 text-xs">
                          {formik.errors.groups}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                Agent signature <span className="text-red-500">*</span>
              </label>
              <div>
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.agent_sign}
                  onChange={(e) => {
                    formik.setFieldValue("agent_sign", e.editor.getData());
                  }}
                />
                {formik.touched.agent_sign && formik.errors.agent_sign && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.agent_sign}
                  </div>
                )}
              </div>
            </div>

            {!location.state && (
              <div className="mt-4 grid grid-cols-1 mb-5">
                <label className="text-xs md:-text-sm lg:text-sm">
                  <input
                    id="send_password_via_email"
                    type="checkbox"
                    checked={formik.values.send_password_via_email === true}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "send_password_via_email",
                        e.target.checked
                      )
                    }
                    className="form-checkbox mr-2 mt-2"
                  />
                  Send password via email
                </label>
              </div>
            )}
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormAgents;
